<script>
	import { createEventDispatcher } from 'svelte';
	import { getApiBase } from './apiBase.js';

	export let selectedStudent = null;
	export let placeholder = 'Search students by name, username, or email...';

	const dispatch = createEventDispatcher();

	let searchTerm = '';
	let searchResults = [];
	let isSearching = false;
	let debounceTimer = null;
	let abortController = null;
	let lastRequestId = 0;

	$: if (!selectedStudent && searchTerm && searchTerm.trim().length >= 2) {
		debouncedSearch(searchTerm.trim());
	} else if (!selectedStudent && (!searchTerm || searchTerm.trim().length < 2)) {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = null;
		if (abortController) abortController.abort();
		abortController = null;
		searchResults = [];
	}

	function debouncedSearch(term) {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => {
			searchStudents(term);
		}, 250);
	}

	async function searchStudents(term) {
		const requestId = ++lastRequestId;
		isSearching = true;
		if (abortController) abortController.abort();
		abortController = new AbortController();
		try {
			const apiBase = getApiBase();
			const response = await fetch(`${apiBase}/studentplayground/api/admin/search-students/`, {
				method: 'POST',
				signal: abortController.signal,
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
				},
				body: JSON.stringify({ search_term: term }),
			});

			if (!response.ok) {
				if (requestId === lastRequestId) searchResults = [];
				return;
			}

			const data = await response.json();
			if (requestId === lastRequestId) {
				searchResults = data?.students || [];
			}
		} catch (e) {
			if (e?.name !== 'AbortError' && requestId === lastRequestId) {
				searchResults = [];
			}
		} finally {
			if (requestId === lastRequestId) isSearching = false;
		}
	}

	function selectStudent(student) {
		dispatch('studentSelected', student);
		searchResults = [];
	}

	function clearSelection() {
		dispatch('studentCleared');
	}
</script>

<div class="student-search">
	<div class="search-row">
		<input
			class="search-input"
			type="text"
			bind:value={searchTerm}
			placeholder={placeholder}
			disabled={!!selectedStudent}
		/>
		{#if isSearching}
			<div class="spinner" aria-label="Searching">⌛</div>
		{/if}
		{#if selectedStudent}
			<button type="button" class="change-btn" on:click={clearSelection}>Change</button>
		{/if}
	</div>

	{#if selectedStudent}
		<div class="selected-card">
			<div class="name">{selectedStudent.first_name} {selectedStudent.last_name}</div>
			<div class="meta">@{selectedStudent.username} • {selectedStudent.email}</div>
			{#if selectedStudent.phone_number}
				<div class="meta">📱 {selectedStudent.phone_number}</div>
			{/if}
		</div>
	{/if}

	{#if searchResults.length > 0 && !selectedStudent}
		<div class="results" role="listbox" aria-label="Student search results">
			{#each searchResults as student}
				<div
					class="result"
					role="option"
					tabindex="0"
					on:click={() => selectStudent(student)}
					on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectStudent(student)}
				>
					<div class="name">{student.first_name} {student.last_name}</div>
					<div class="meta">@{student.username} • {student.email}</div>
					{#if student.phone_number}
						<div class="meta">📱 {student.phone_number}</div>
					{/if}
				</div>
			{/each}
		</div>
	{:else if !selectedStudent && !isSearching && searchTerm && searchTerm.trim().length >= 2}
		<div class="no-results">No students found.</div>
	{/if}
</div>

<style>
	.student-search {
		position: relative;
		width: 100%;
	}

	.search-row {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	.search-input {
		flex: 1;
		width: 100%;
		padding: 12px 14px;
		border: 1px solid #ccc;
		border-radius: 8px;
		font-size: 14px;
	}

	.search-input:disabled {
		background: #f7f7f7;
		cursor: not-allowed;
	}

	.spinner {
		font-size: 12px;
		color: #666;
	}

	.change-btn {
		padding: 10px 14px;
		border: 1px solid #ccc;
		background: white;
		border-radius: 8px;
		cursor: pointer;
		white-space: nowrap;
	}

	.selected-card {
		margin-top: 12px;
		padding: 12px 14px;
		border-radius: 8px;
		border: 1px solid #bcdcff;
		background: #e7f3ff;
	}

	.results {
		margin-top: 8px;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		background: white;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		max-height: 320px;
		overflow: auto;
	}

	.result {
		padding: 10px 12px;
		border-bottom: 1px solid #f0f0f0;
		cursor: pointer;
		outline: none;
	}

	.result:hover,
	.result:focus {
		background: #f8f9fa;
	}

	.result:last-child {
		border-bottom: none;
	}

	.name {
		font-weight: 600;
		color: #222;
	}

	.meta {
		color: #666;
		font-size: 12px;
		margin-top: 2px;
	}

	.no-results {
		margin-top: 8px;
		padding: 12px 14px;
		border-radius: 8px;
		background: #f8f9fa;
		border: 1px solid #e0e0e0;
		color: #666;
	}
</style>

